import { Icon, Link, Paragraph, Strong } from '@customerio/pluma-components/react';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { generatePath } from '@/services/router/generate-path';
import type { SqlImportWriteInput } from '@/services/db/sql-import/mutations';
import { ImportType } from '@/services/db/sql-import/entity';
import { capitalize, isEmptyString } from '@/utils/string';

type ImportSummaryProps = {
  workspaceId: string;
  sqlImport: Pick<
    SqlImportWriteInput,
    'import_type' | 'id_field_name' | 'segment_id' | 'segment_name' | 'clear_empty_cells' | 'ignore_suppress_err'
  >;
  /** "People" or the object type's name; see `sqlImportSyncTarget`. */
  syncTarget: string;
  /** The segment the import writes to, when it targets an existing one. */
  segment?: { id: string; name: string } | null;
  /** Start the sentence with a capital letter, as a standalone paragraph does. */
  withCapital?: boolean;
  /** "adding … and updating" for a sentence about a running import, instead of "add … and update". */
  isProgressive?: boolean;
  /** Name the identifier the rows are matched on. */
  withIdentifyBy?: boolean;
};

/**
 * The three-line description of what a sync does: what it adds and updates, how
 * it treats empty cells, and how it treats suppressed people. Used by the wizard
 * review step and by the activate, pause and delete confirmations.
 */
export function ImportSummary({
  workspaceId,
  sqlImport,
  syncTarget,
  segment = null,
  withCapital = false,
  isProgressive = false,
  withIdentifyBy = false,
}: ImportSummaryProps) {
  const taxonomy = useProductTaxonomy();
  const target = syncTarget.toLowerCase();
  const add = isProgressive ? 'adding' : 'add';
  const update = isProgressive ? 'updating' : 'update';
  const people = taxonomy.term('people.plural');
  const importsToSegment = sqlImport.import_type === ImportType.workspaceAndSegment;

  return (
    <span data-test-sql-import-summary>
      <Paragraph mb="200" display={withCapital ? undefined : 'inline-block'}>
        {withCapital ? capitalize(add) : add} new {target} and {update} existing {target}
        {withIdentifyBy && !isEmptyString(sqlImport.id_field_name) ? ` by ${sqlImport.id_field_name}` : ''} in this
        workspace
        {importsToSegment ? (
          <>
            {' '}
            and <Icon name="segment-manual" display="inline-block" mr="050" />
            {segment != null ? (
              <Link
                href={generatePath('/workspaces/:workspaceId/journeys/segments/:segmentId', {
                  workspaceId,
                  segmentId: segment.id,
                })}
                variant="secondary"
                isUnderlined={false}
              >
                <Strong>{segment.name}</Strong>
              </Link>
            ) : (
              <Strong>{sqlImport.segment_name}</Strong>
            )}
          </>
        ) : null}
      </Paragraph>
      {/* `clear_empty_cells` names the settings choice "Ignore them": an empty cell clears nothing. */}
      <Paragraph mb="200">
        {sqlImport.clear_empty_cells === true
          ? 'Ignore empty values'
          : 'Delete existing attribute value for empty value'}
      </Paragraph>
      <Paragraph>
        {sqlImport.ignore_suppress_err === false
          ? `Ignore suppressed ${people} and mark unsuccessful`
          : `Ignore suppressed ${people} and mark successful`}
      </Paragraph>
    </span>
  );
}
